import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { 
  MessageCircle, 
  Send, 
  User, 
  GraduationCap,
  ArrowLeft,
  Search,
  Clock,
  Check,
  CheckCheck,
  Loader2,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function TeacherMessages() {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [initialMessage, setInitialMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API}/teacher/messages/threads`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setThreads(data.threads || []);
      }
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await fetch(`${API}/teacher/messages/threads/${threadId}`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
        // Update thread unread count locally
        setThreads(prev => prev.map(t => 
          t.id === threadId ? { ...t, teacher_unread_count: 0 } : t
        ));
      }
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchParents = async () => {
    try {
      const response = await fetch(`${API}/teacher/messages/parents`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setParents(data.parents || []);
      }
    } catch (error) {
      toast.error("Failed to load parents");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API}/teacher/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          thread_id: selectedThread.id,
          content: newMessage.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        setNewMessage("");
        // Update thread's last message
        setThreads(prev => prev.map(t => 
          t.id === selectedThread.id 
            ? { ...t, last_message: data.message.content, last_message_at: data.message.sent_at }
            : t
        ));
      } else {
        toast.error(data.error || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartNewChat = async () => {
    if (!selectedParent || !selectedChild || !initialMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API}/teacher/messages/threads/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          parent_id: selectedParent.parent_id,
          student_id: selectedChild.id,
          initial_message: initialMessage.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Conversation started!");
        setShowNewChatModal(false);
        setSelectedParent(null);
        setSelectedChild(null);
        setInitialMessage("");
        fetchThreads();
        // Select the new thread
        if (data.thread) {
          setSelectedThread(data.thread);
        }
      } else {
        toast.error(data.error || "Failed to start conversation");
      }
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const openNewChatModal = () => {
    fetchParents();
    setShowNewChatModal(true);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const filteredThreads = threads.filter(t => 
    t.parent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = threads.reduce((sum, t) => sum + (t.teacher_unread_count || 0), 0);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-emerald-600" />
            Parent Messages
            {totalUnread > 0 && (
              <Badge className="bg-red-500 text-white ml-2">{totalUnread}</Badge>
            )}
          </h1>
          <p className="text-slate-500 text-sm">Communicate with parents about their children</p>
        </div>
        <Button onClick={openNewChatModal} className="bg-emerald-600 hover:bg-emerald-700">
          <MessageCircle className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Thread List */}
        <Card className="w-80 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Search conversations..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-4 text-center text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <Button variant="link" onClick={openNewChatModal} className="text-emerald-600">
                  Message a parent
                </Button>
              </div>
            ) : (
              filteredThreads.map((thread) => (
                <div
                  key={thread.id}
                  onClick={() => setSelectedThread(thread)}
                  className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedThread?.id === thread.id ? 'bg-emerald-50 border-l-4 border-l-emerald-600' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 bg-emerald-100">
                      <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                        {getInitials(thread.parent_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-800 truncate">
                          {thread.parent_name}
                        </span>
                        <span className="text-xs text-slate-400">
                          {formatTime(thread.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <GraduationCap className="w-3 h-3" />
                        <span>Re: {thread.student_name}</span>
                      </div>
                      <p className="text-sm text-slate-500 truncate mt-1">
                        {thread.last_message || "No messages yet"}
                      </p>
                    </div>
                    {thread.teacher_unread_count > 0 && (
                      <Badge className="bg-emerald-600 text-white text-xs">
                        {thread.teacher_unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </Card>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col">
          {selectedThread ? (
            <>
              <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden"
                    onClick={() => setSelectedThread(null)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <Avatar className="w-10 h-10 bg-emerald-100">
                    <AvatarFallback className="bg-emerald-100 text-emerald-600">
                      {getInitials(selectedThread.parent_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-800">{selectedThread.parent_name}</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <GraduationCap className="w-3 h-3" />
                      Parent of {selectedThread.student_name}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'teacher' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        msg.sender_type === 'teacher' 
                          ? 'bg-emerald-600 text-white rounded-l-lg rounded-tr-lg' 
                          : 'bg-slate-100 text-slate-800 rounded-r-lg rounded-tl-lg'
                      } p-3`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${
                          msg.sender_type === 'teacher' ? 'text-emerald-200' : 'text-slate-400'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {formatTime(msg.sent_at)}
                          {msg.sender_type === 'teacher' && (
                            msg.read_at 
                              ? <CheckCheck className="w-3 h-3 ml-1" />
                              : <Check className="w-3 h-3 ml-1" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="resize-none min-h-[60px]"
                    rows={2}
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="bg-emerald-600 hover:bg-emerald-700 self-end"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm">or message a parent</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Chat Modal */}
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Message Parent</DialogTitle>
            <DialogDescription>
              Send a message to a student's parent
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Select Parent</label>
              <Select 
                value={selectedParent?.parent_id || ""} 
                onValueChange={(value) => {
                  const parent = parents.find(p => p.parent_id === value);
                  setSelectedParent(parent);
                  setSelectedChild(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a parent" />
                </SelectTrigger>
                <SelectContent>
                  {parents.map((parent) => (
                    <SelectItem key={parent.parent_id} value={parent.parent_id}>
                      {parent.parent_name}
                      <span className="text-slate-400 ml-2">
                        ({parent.children?.map(c => c.name).join(", ")})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedParent && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Regarding Student</label>
                <Select 
                  value={selectedChild?.id || ""} 
                  onValueChange={(value) => {
                    const child = selectedParent.children.find(c => c.id === value);
                    setSelectedChild(child);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedParent.children.map((child) => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.name} (Grade {child.grade}{child.section ? ` - ${child.section}` : ''})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Message</label>
              <Textarea
                placeholder="Write your message here..."
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChatModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartNewChat}
              disabled={!selectedParent || !selectedChild || !initialMessage.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
